﻿﻿﻿﻿/**
 * O que faz: Exibe score, resumo, recomendações agrupadas por dimensão e debug da análise SEO de Perfil.
 * Onde usa: SeoProfileAnalyzerPage e SeoProfileOnboardingStep.
 * Conexões: SeoProfileAnalysisResult, structuredAnalysis em extracted_signals.
 * Tabelas: seo_profile_analyses (dados já carregados pelo hook).
 * Data: 14/05/2026
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, ChevronUp, Copy, Download, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SeoProfileExportDialog } from './SeoProfileExportDialog';
import type {
  SeoDimensionStatus,
  SeoImpact,
  SeoProfileAnalysisResult,
  SeoStructuredAnalysis,
  SeoStructuredDimension,
} from '@/types/seoProfile';

interface SeoProfileResultProps {
  analysis: SeoProfileAnalysisResult | null;
  showDebug?: boolean;
}

const sourceLabel: Record<string, string> = {
  website: 'Site',
  google_business: 'Google Meu Negócio',
  instagram: 'Instagram',
  doctoralia: 'Doctoralia',
  general_search: 'Busca geral',
};

const dimensionLabel: Record<string, string> = {
  categorias: 'Categorias',
  informacoes_basicas: 'Nome, endereço e telefone',
  descricao: 'Descrição do perfil',
  servicos: 'Serviços cadastrados',
  atributos: 'Atributos e comodidades',
  reviews_volume_qualidade: 'Avaliações — volume e qualidade',
  respostas_reviews: 'Respostas às avaliações',
  posts: 'Publicações no perfil',
  fotos: 'Fotos',
  qa: 'Perguntas e respostas',
  perguntas_e_respostas: 'Perguntas e respostas',
  recursos_avancados: 'Recursos avançados',
  seo_tecnico: 'SEO técnico',
  seo_local: 'SEO local',
  conversao: 'Conversão',
  schema_e_dados_estruturados: 'Dados estruturados',
  ai_seo: 'Otimização para IA',
  conteudo_e_clareza: 'Conteúdo e clareza',
  posicionamento: 'Posicionamento',
  bio_e_cta: 'Bio e chamada para ação',
  prova_social: 'Prova social',
  conteudo: 'Conteúdo',
  completude_do_perfil: 'Completude do perfil',
  especialidade_e_posicionamento: 'Especialidade e posicionamento',
  agendamento: 'Agendamento',
  identificacao_da_clinica: 'Identificação da clínica',
  presenca_local: 'Presença local',
  reputacao_publica: 'Reputação pública',
  lacunas_de_dados: 'Informações em falta',
  proximas_analises_recomendadas: 'Próximas análises recomendadas',
  dados_ausentes: 'Dados não encontrados',
};

const statusConfig: Record<
  SeoDimensionStatus | string,
  { label: string; variant: 'destructive' | 'default' | 'secondary' | 'outline'; dot: string }
> = {
  critico: { label: 'Crítico', variant: 'destructive', dot: 'bg-red-500' },
  atencao: { label: 'Atenção', variant: 'default', dot: 'bg-amber-400' },
  ok: { label: 'OK', variant: 'secondary', dot: 'bg-emerald-500' },
  excelente: { label: 'Excelente', variant: 'secondary', dot: 'bg-emerald-600' },
};

const severityVariant: Record<SeoImpact | string, 'destructive' | 'default' | 'secondary'> = {
  alta: 'destructive',
  alto: 'destructive',
  media: 'default',
  medio: 'default',
  baixa: 'secondary',
  baixo: 'secondary',
};

function scoreColor(score: number): string {
  if (score >= 70) return 'text-emerald-600';
  if (score >= 40) return 'text-amber-500';
  return 'text-red-500';
}

function scoreRingColor(score: number): string {
  if (score >= 70) return 'stroke-emerald-500';
  if (score >= 40) return 'stroke-amber-400';
  return 'stroke-red-500';
}

function ScoreRing({ score }: { score: number }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="relative flex items-center justify-center">
        <svg width="96" height="96" className="-rotate-90">
          <circle cx="48" cy="48" r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/30" />
          <circle
            cx="48"
            cy="48"
            r={r}
            fill="none"
            strokeWidth="6"
            strokeDasharray={`${fill} ${circ - fill}`}
            strokeLinecap="round"
            className={cn('transition-all duration-700', scoreRingColor(score))}
          />
        </svg>
        <span className={cn('absolute text-2xl font-bold tabular-nums', scoreColor(score))}>{score}</span>
      </div>
      <span className="text-[11px] text-muted-foreground">de 100</span>
    </div>
  );
}

function scoreLevelConfig(score: number): { label: string; icon: React.ReactNode; bg: string; text: string } {
  if (score >= 70) return { label: 'Boa presença digital', icon: <TrendingUp className="h-3.5 w-3.5" />, bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-400' };
  if (score >= 40) return { label: 'Presença em desenvolvimento', icon: <Minus className="h-3.5 w-3.5" />, bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-400' };
  return { label: 'Baixa presença digital', icon: <TrendingDown className="h-3.5 w-3.5" />, bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-400' };
}

// Distribuição máxima de pontos por dimensão (espelha scoring.ts do Google Meu Negócio
// + estimativas para website e demais fontes). Usado para gamificação das melhorias.
const DIMENSION_MAX_POINTS: Record<string, number> = {
  categorias: 12,
  informacoes_basicas: 15,
  descricao: 10,
  servicos: 10,
  atributos: 5,
  reviews_volume_qualidade: 20,
  respostas_reviews: 8,
  posts: 8,
  fotos: 6,
  qa: 3,
  perguntas_e_respostas: 3,
  recursos_avancados: 3,
  seo_tecnico: 30,
  seo_local: 25,
  conversao: 16,
  schema_e_dados_estruturados: 12,
  ai_seo: 10,
  conteudo_e_clareza: 8,
  posicionamento: 15,
  bio_e_cta: 12,
  prova_social: 10,
  conteudo: 10,
  completude_do_perfil: 18,
  especialidade_e_posicionamento: 12,
  agendamento: 10,
};

function pointsForImprovement(dimensionKey: string, impacto: string | null | undefined): number {
  const maxDim = DIMENSION_MAX_POINTS[dimensionKey] ?? 8;
  const value = (impacto ?? '').toLowerCase();
  if (value === 'alto' || value === 'alta') return Math.min(maxDim, 8);
  if (value === 'medio' || value === 'media' || value === 'médio' || value === 'média') return Math.min(maxDim, 4);
  if (value === 'baixo' || value === 'baixa') return Math.min(maxDim, 2);
  return Math.min(maxDim, 3);
}

function extractBusinessName(analysis: SeoProfileAnalysisResult): string | null {
  const signals = analysis.extracted_signals as Record<string, unknown> | undefined;
  if (!signals) return null;
  const gbp = signals.normalizedGbpPayload as Record<string, unknown> | undefined;
  const negocio = gbp?.negocio as Record<string, unknown> | undefined;
  if (typeof negocio?.nome === 'string' && negocio.nome.trim()) return negocio.nome.trim();
  const platforms = signals.platforms as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(platforms)) {
    const google = platforms.find((p) => p?.channel === 'google');
    if (typeof google?.name === 'string' && (google.name as string).trim()) return (google.name as string).trim();
    const first = platforms[0];
    if (typeof first?.name === 'string' && (first.name as string).trim()) return (first.name as string).trim();
  }
  const website = signals.website as Record<string, unknown> | undefined;
  if (typeof website?.title === 'string' && (website.title as string).trim()) return (website.title as string).trim();
  return null;
}

function totalPotentialPoints(dimensions: SeoStructuredDimension[]): number {
  let total = 0;
  for (const dim of dimensions) {
    for (const imp of dim.melhorias_sugeridas ?? []) {
      total += pointsForImprovement(dim.dimensao, imp.impacto_esperado as string | null | undefined);
    }
  }
  return total;
}

function formatDimensionTitle(value: string): string {
  return dimensionLabel[value] ?? value.replace(/_/g, ' ');
}

function generateMarkdown(analysis: SeoProfileAnalysisResult, businessName: string | null): string {
  const lines: string[] = [];
  const title = businessName ? `Diagnóstico SEO: ${businessName}` : 'Diagnóstico de Presença Digital';
  lines.push(`# ${title}`, '');

  const parts: string[] = [];
  if (typeof analysis.overall_score === 'number') parts.push(`**Pontuação:** ${analysis.overall_score}/100`);
  if (analysis.analysis_source) parts.push(`**Fonte:** ${sourceLabel[analysis.analysis_source] ?? analysis.analysis_source}`);
  if (analysis.created_at) parts.push(`**Data:** ${new Date(analysis.created_at).toLocaleDateString('pt-BR')}`);
  if (parts.length) lines.push(parts.join('  ·  '), '');

  if (analysis.summary) {
    lines.push('## Resumo', '', analysis.summary, '');
  }

  const structured = analysis.extracted_signals?.structuredAnalysis ?? null;

  if (structured?.resumo_executivo) {
    const exec = structured.resumo_executivo;
    if (exec.pontos_criticos?.length) {
      lines.push('## Pontos Críticos', '');
      for (const p of exec.pontos_criticos) lines.push(`- ${p}`);
      lines.push('');
    }
    if (exec.vitorias_rapidas?.length) {
      lines.push('## Vitórias Rápidas', '');
      for (const v of exec.vitorias_rapidas) lines.push(`- ${v}`);
      lines.push('');
    }
  }

  if (structured?.analise_por_dimensao?.length) {
    lines.push('## Análise por Dimensão', '');
    const statusEmoji: Record<string, string> = { critico: '🔴 Crítico', atencao: '🟡 Atenção', ok: '🟢 OK', excelente: '✅ Excelente' };
    for (const dim of structured.analise_por_dimensao) {
      lines.push(`### ${formatDimensionTitle(dim.dimensao)} — ${statusEmoji[dim.status] ?? dim.status}`, '');
      if (dim.pontos_negativos?.length) {
        lines.push('**Problemas identificados:**', '');
        for (const neg of dim.pontos_negativos) {
          lines.push(`- ${neg.problema}`);
          if (neg.impacto_no_ranking) lines.push(`  - *${neg.impacto_no_ranking}*`);
        }
        lines.push('');
      }
      if (dim.melhorias_sugeridas?.length) {
        lines.push('**Melhorias sugeridas:**', '');
        dim.melhorias_sugeridas.forEach((imp, i) => {
          const pts = pointsForImprovement(dim.dimensao, imp.impacto_esperado as string | null | undefined);
          const meta = [
            imp.impacto_esperado ? `Impacto: ${imp.impacto_esperado}` : '',
            imp.esforco ? `Esforço: ${imp.esforco}` : '',
            `+${pts} pts`,
          ].filter(Boolean).join(' · ');
          lines.push(`${i + 1}. **${imp.acao}** *(${meta})*`);
          if (imp.como_executar) lines.push(`   > ${imp.como_executar}`);
        });
        lines.push('');
      }
    }
  }

  if (structured?.roadmap_priorizado) {
    const road = structured.roadmap_priorizado;
    lines.push('## Roadmap de Ação', '');
    const weeks: Array<[string, string[] | undefined]> = [
      ['Semana 1', road.semana_1],
      ['Semana 2', road.semana_2],
      ['Semana 3', road.semana_3],
      ['Semana 4', road.semana_4],
    ];
    for (const [label, tasks] of weeks) {
      if (tasks?.length) {
        lines.push(`### ${label}`, '');
        for (const t of tasks) lines.push(`- [ ] ${t}`);
        lines.push('');
      }
    }
    if ((road as Record<string, unknown>).execucao_continua) {
      const continua = (road as Record<string, string[]>).execucao_continua;
      if (continua?.length) {
        lines.push('### Execução Contínua', '');
        for (const t of continua) lines.push(`- ${t}`);
        lines.push('');
      }
    }
  }

  if (!structured && analysis.recommendations?.length) {
    lines.push('## Recomendações', '');
    for (const rec of analysis.recommendations) {
      lines.push(`- **[${rec.priority}] ${rec.title}**`);
      if (rec.why) lines.push(`  - *${rec.why}*`);
      if (rec.action) lines.push(`  - ${rec.action}`);
    }
    lines.push('');
  }

  lines.push('---', `*Gerado por Diagnóstico SEO · ${new Date().toLocaleDateString('pt-BR')}*`);
  return lines.join('\n');
}

function DimensionCard({ dimension }: { dimension: SeoStructuredDimension }) {
  const [open, setOpen] = useState(dimension.status === 'critico' || dimension.status === 'atencao');
  const negatives = dimension.pontos_negativos ?? [];
  const improvements = dimension.melhorias_sugeridas ?? [];
  const cfg = statusConfig[dimension.status ?? ''] ?? { label: dimension.status, variant: 'outline' as const, dot: 'bg-gray-400' };

  return (
    <Card className="overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', cfg.dot)} />
          <span className="text-sm font-medium truncate">{formatDimensionTitle(dimension.dimensao)}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={cfg.variant} className="text-xs">{cfg.label}</Badge>
          {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </button>

      {open && (negatives.length > 0 || improvements.length > 0) && (
        <CardContent className="space-y-4 border-t pt-4 pb-4 text-sm">
          {negatives.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">O que está prejudicando</p>
              <ul className="space-y-2">
                {negatives.map((neg, i) => (
                  <li key={i} className="rounded-xl border border-red-100 bg-red-50/50 p-3">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Badge variant={severityVariant[neg.severidade] ?? 'secondary'} className="text-xs">
                        Impacto {String(neg.severidade ?? 'médio')}
                      </Badge>
                    </div>
                    <p className="font-medium text-sm">{neg.problema}</p>
                    {neg.impacto_no_ranking && (
                      <p className="mt-1 text-xs text-muted-foreground">{neg.impacto_no_ranking}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {improvements.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">O que fazer</p>
              <ul className="space-y-2">
                {improvements.map((imp, i) => {
                  const points = pointsForImprovement(dimension.dimensao, imp.impacto_esperado as string | null | undefined);
                  return (
                    <li key={i} className="rounded-xl border bg-muted/20 p-3">
                      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {imp.impacto_esperado && (
                            <Badge variant={severityVariant[imp.impacto_esperado] ?? 'secondary'} className="text-xs">
                              Ganho {String(imp.impacto_esperado)}
                            </Badge>
                          )}
                          {imp.esforco && (
                            <Badge variant="outline" className="text-xs">Esforço {String(imp.esforco)}</Badge>
                          )}
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:ring-emerald-900/50">
                          <TrendingUp className="h-3 w-3" />
                          +{points} pts
                        </span>
                      </div>
                      <p className="font-medium">{imp.acao}</p>
                      {imp.como_executar && (
                        <p className="mt-1 text-xs text-muted-foreground">{imp.como_executar}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function StructuredView({ structured }: { structured: SeoStructuredAnalysis }) {
  const dimensions = structured.analise_por_dimensao ?? [];
  const summary = structured.resumo_executivo;
  const roadmap = structured.roadmap_priorizado;

  const criticalDims = dimensions.filter((d) => d.status === 'critico');
  const otherDims = dimensions.filter((d) => d.status !== 'critico');
  const sortedDims = [...criticalDims, ...otherDims];

  return (
    <div className="space-y-4">
      {summary && (summary.pontos_criticos?.length || summary.vitorias_rapidas?.length) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {summary.pontos_criticos?.length ? (
            <Card className="border-red-200 bg-red-50/40 shadow-sm">
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="text-sm font-semibold text-red-800">Pontos críticos</CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <ul className="space-y-1.5">
                  {summary.pontos_criticos.map((point, i) => (
                    <li key={i} className="flex gap-2 text-sm text-red-700">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}
          {summary.vitorias_rapidas?.length ? (
            <Card className="border-emerald-200 bg-emerald-50/40 shadow-sm">
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="text-sm font-semibold text-emerald-800">Vitórias rápidas</CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <ul className="space-y-1.5">
                  {summary.vitorias_rapidas.map((point, i) => (
                    <li key={i} className="flex gap-2 text-sm text-emerald-700">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      <div className="space-y-2">
        {sortedDims.map((dimension, index) => (
          <DimensionCard key={`${dimension.dimensao}-${index}`} dimension={dimension} />
        ))}
      </div>

      {roadmap && (
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Plano de ação — próximas 4 semanas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm pb-4">
            {(['semana_1', 'semana_2', 'semana_3', 'semana_4'] as const).map((week, idx) => {
              const items = roadmap[week];
              if (!items?.length) return null;
              return (
                <div key={week} className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Semana {idx + 1}</p>
                  <ul className="space-y-1">
                    {items.map((item, i) => (
                      <li key={i} className="flex gap-2 text-muted-foreground">
                        <span className="mt-0.5 shrink-0 text-emerald-500">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {roadmap.execucao_continua?.length ? (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Execução contínua</p>
                <ul className="space-y-1">
                  {roadmap.execucao_continua.map((item, i) => (
                    <li key={i} className="flex gap-2 text-muted-foreground">
                      <span className="mt-0.5 shrink-0 text-emerald-500">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FlatRecommendationsView({ recommendations }: { recommendations: SeoProfileAnalysisResult['recommendations'] }) {
  const priorityVariant: Record<string, 'destructive' | 'default' | 'secondary'> = {
    alta: 'destructive',
    media: 'default',
    baixa: 'secondary',
  };

  return (
    <div className="space-y-2">
      {recommendations.map((rec, index) => (
        <Card key={`${rec.channel}-${index}`} className="shadow-sm">
          <CardContent className="pt-4 pb-4 space-y-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={priorityVariant[rec.priority] ?? 'secondary'} className="text-xs">
                Prioridade {rec.priority}
              </Badge>
              <Badge variant="outline" className="text-xs">{rec.channel}</Badge>
            </div>
            <p className="font-medium">{rec.title}</p>
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Por que importa:</span> {rec.why}</p>
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Ação:</span> {rec.action}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function SeoProfileResult({ analysis, showDebug = false }: SeoProfileResultProps) {
  if (!analysis) {
    return (
      <Card className="border-dashed shadow-sm">
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="text-4xl">🔍</div>
          <p className="font-medium">Nenhuma análise ainda</p>
          <p className="max-w-xs text-sm text-muted-foreground">
            Selecione uma fonte ao lado e inicie o diagnóstico para ver os resultados aqui.
          </p>
        </CardContent>
      </Card>
    );
  }

  const structured = analysis.extracted_signals?.structuredAnalysis ?? null;
  const hasStructuredContent = Boolean(
    structured && (structured.analise_por_dimensao?.length || structured.resumo_executivo || structured.roadmap_priorizado),
  );
  const score = analysis.overall_score ?? 0;
  const [exportOpen, setExportOpen] = useState(false);
  const [markdownCopied, setMarkdownCopied] = useState(false);
  const businessName = extractBusinessName(analysis);
  const potentialGain = structured ? totalPotentialPoints(structured.analise_por_dimensao ?? []) : 0;

  return (
    <div className="space-y-4">
      {/* Score header */}
      <Card className="shadow-sm">
        <CardContent className="pt-5 pb-5">
          <div className="flex flex-row items-start gap-3 text-left sm:items-center sm:gap-5">
            <div className="shrink-0">
              <ScoreRing score={score} />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
              {businessName && (
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Análise para</p>
              )}
              <h2 className="text-base font-bold leading-tight sm:text-lg">
                {businessName ?? 'Seu diagnóstico atual'}
              </h2>
              {businessName && (
                <p className="text-xs font-medium text-muted-foreground sm:text-sm">Seu diagnóstico atual</p>
              )}
              {(() => {
                const lvl = scoreLevelConfig(score);
                return (
                  <span className={cn('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium', lvl.bg, lvl.text)}>
                    {lvl.icon}
                    {lvl.label}
                  </span>
                );
              })()}
              {potentialGain > 0 && (
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  <TrendingUp className="mr-1 inline h-3 w-3" />
                  Implementar todas as ações pode somar até <span className="font-bold">+{potentialGain} pts</span>
                </p>
              )}
              {analysis.summary && (
                <p className="hidden text-sm text-muted-foreground leading-relaxed sm:block">{analysis.summary}</p>
              )}
            </div>
            <div className="shrink-0 self-start flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const md = generateMarkdown(analysis, businessName);
                  navigator.clipboard.writeText(md).then(() => {
                    setMarkdownCopied(true);
                    setTimeout(() => setMarkdownCopied(false), 2000);
                  });
                }}
                className="gap-1.5"
                aria-label="Copiar como Markdown"
                title="Copiar como Markdown"
              >
                {markdownCopied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">{markdownCopied ? 'Copiado!' : 'Markdown'}</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExportOpen(true)}
                className="gap-1.5"
                aria-label="Exportar relatório"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Exportar</span>
              </Button>
            </div>
          </div>
          {analysis.summary && (
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed sm:hidden">{analysis.summary}</p>
          )}
        </CardContent>
      </Card>

      {/* Main content */}
      {hasStructuredContent && structured ? (
        <StructuredView structured={structured} />
      ) : (
        <FlatRecommendationsView recommendations={analysis.recommendations ?? []} />
      )}

      {/* Debug — só exibe quando explicitamente solicitado */}
      {showDebug && analysis.debug_events?.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm">Debug da análise</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 pb-4">
            {analysis.debug_events.map((event, index) => (
              <div key={`${event.step}-${index}`} className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={event.ok ? 'secondary' : 'destructive'} className="text-xs">{event.ok ? 'ok' : 'falha'}</Badge>
                <span className="font-mono">{event.step}</span>
                {event.provider && <span className="text-muted-foreground">{event.provider}</span>}
                {typeof event.status === 'number' && <span className="text-muted-foreground">status {event.status}</span>}
                {typeof event.elapsedMs === 'number' && <span className="text-muted-foreground">{event.elapsedMs}ms</span>}
                {event.message && <span className="text-muted-foreground">{event.message}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <SeoProfileExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        analysis={analysis}
        clinicName={businessName ?? undefined}
      />
    </div>
  );
}









